import { makeUrl } from '../constant';

export const CHECK_USER_REQUEST = '/api/v1/auth/check/';
export const LOGOUT_REQUEST = '/api/v1/auth/logout/';
export const LOGIN_REQUEST = '/api/v1/auth/admin/login/';
export const REFRESH_SESSION_REQUEST = '/api/v1/auth/refresh/';
export const INVITE_USER_REQUEST = '/api/v1/user/invite/';
export const GET_USER_REQUEST = '/api/v1/user/';
export const GET_PRODUCT_SEARCH = '/api/v1/product/search/';
export const GET_PRODUCT_BY_ID = '/api/v1/product/';
export const GET_CATEGORIES = '/api/v1/category/';
export const REQUEST_INFO = '/api/v1/request/';
export const PRODUCT_INFO = '/api/v1/product/';
export const INTEGRATION_INFO = '/api/v1/integration/';
export const GET_INFO_FTS = '/api/v1/customer/fts/';
export const MAKE_CUSTOMER_REQUEST = '/api/v1/customer/';
export const ADMIN_CREATE_CUSTOMER_USER = '/api/v1/user/admin/create-customer/';
export const GET_STATISTIC = '/api/v1/statistic/';
export const CHANGE_PASSWORD_REQUEST = '/api/v1/password/request/';
export const REJECT_USER_REQUEST = '/api/v1/user/';
export const UNREJECT_USER_REQUEST = '/api/v1/user/';
export const GET_STOCKS = '/api/v1/stock/';
export const GET_ORDERS = '/api/v1/order/';
export const ADMIN_CREATE_ORDER = '/api/v1/order/admin/';
export const SUPPLIER_ORDERS_URL = '/api/v1/order/supplier-orders/';
export const ADMIN_CREATE_SUPPLIER_ORDER = '/api/v1/order/supplier-orders/';
export const CUSTOMER_ORDERS_SHORT_LIST_URL = '/api/v1/order/short-list/';
export const GET_ORDER_LOGS = '/api/v1/order/logs/cleanup/';
export const GET_ADMIN_AUTH_LOGS = '/api/v1/auth/logs/admin/';
export const CONFIGS_URL = '/api/v1/application/config/';
export const BUSINESS_CALENDAR_URL = '/api/v1/application/business-calendar/';
export const SITE_CONTENT_URL = '/api/v1/site-content/';
export const LOAD_PRODUCTS_URL = '/api/v1/application/product/load/';
export const BANNERS_URL = '/api/v1/application/banner/';
export const SUPPLIER_PRODUCT_STATUSES_URL = '/api/v1/application/supplier-product-status/';
export const CSRF_URL = '/api/v1/auth/csrf/';
export const SUPPLIERS_URL = '/api/v1/supplier/';
export const AUTOBASE_PREVIEW_URL = '/api/v1/application/autobase/preview/';
export const AUTOBASE_CONTINUE_URL = '/api/v1/application/autobase/continue/';
export const AUTOBASE_LOG_STATE_URL = '/api/v1/application/autobase/log-state/';

const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) {
        return {};
    }
    return JSON.parse(text);
};

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        const part = parts.pop();
        if (part) {
            return decodeURIComponent(part.split(';').shift());
        }
    }
    return null;
};

const getCsrfTokenFromCookie = () => getCookie('csrftoken');

const ensureCsrfToken = async () => {
    if (getCsrfTokenFromCookie()) {
        return;
    }
    await fetch(makeUrl([CSRF_URL]), {
        credentials: 'include',
        method: 'GET',
    });
};

const buildJsonHeaders = () => {
    const headers = {
        "Content-Type": "application/json",
    };
    const csrf = getCsrfTokenFromCookie();
    if (csrf) {
        headers["X-CSRFToken"] = csrf;
    }
    return headers;
};

const buildCsrfOnlyHeaders = () => {
    const headers = {};
    const csrf = getCsrfTokenFromCookie();
    if (csrf) {
        headers["X-CSRFToken"] = csrf;
    }
    return headers;
};

const refreshAuthSession = async () => {
    await ensureCsrfToken();
    const response = await fetch(makeUrl([REFRESH_SESSION_REQUEST]), {
        credentials: 'include',
        method: 'POST',
        headers: buildCsrfOnlyHeaders(),
    });
    if (!response.ok) {
        throw new Error('Unable to refresh authentication');
    }
};

const executeRequest = (requestFactory, parser, successCallback, errorCallback, options = {}) => {
    const { skipOkCheck = false } = options;

    const run = async (shouldRetry = true) => {
        await ensureCsrfToken();
        const response = await requestFactory();

        if (response.status === 401 && shouldRetry) {
            try {
                await refreshAuthSession();
                return run(false);
            } catch (refreshError) {
                throw refreshError;
            }
        }

        if (!skipOkCheck && !response.ok) {
            const text = await response.text();
            const message = text || `Request failed with status ${response.status}`;
            throw new Error(message);
        }

        if (parser) {
            return parser(response);
        }

        return response;
    };

    run()
        .then((result) => successCallback(result))
        .catch((error) => errorCallback(error));
};

export const getFetchRequest = (url, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'GET',
        headers: buildJsonHeaders(),
    });
    executeRequest(requestFactory, parseJsonResponse, successCallback, errorCallback);
};

export const postFetchRequest = (url, body, successCallback, errorCallback, withoutJSON=false) => {
    const parser = (body && !withoutJSON) ? parseJsonResponse : (response) => response;
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify(body),
    });
    executeRequest(requestFactory, parser, successCallback, errorCallback);
};

export const postFormRequest = (url, formData, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'POST',
        headers: buildCsrfOnlyHeaders(),
        body: formData,
    });
    executeRequest(requestFactory, parseJsonResponse, successCallback, errorCallback);
};

export const postFetchLoadRequest = (url, body, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'POST',
        headers: buildCsrfOnlyHeaders(),
        body,
    });
    executeRequest(
        requestFactory,
        (response) => response,
        successCallback,
        errorCallback,
        { skipOkCheck: true },
    );
};


export const postProductResource = (productId, resourceData, successCallback, errorCallback) => {
  postFetchRequest(
    makeUrl([GET_PRODUCT_BY_ID, productId, '/resource/']), // Формируем URL: /api/v1/product/{id}/resource/
    resourceData, // { type: "DATASHEET", url: "https://..." }
    successCallback,
    errorCallback
  );
};

export const putFetchRequest = (url, body, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'PUT',
        headers: buildJsonHeaders(),
        body: JSON.stringify(body),
    });
    executeRequest(requestFactory, parseJsonResponse, successCallback, errorCallback);
};

export const patchFetchRequest = (url, body, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'PATCH',
        headers: buildJsonHeaders(),
        body: JSON.stringify(body),
    });
    executeRequest(requestFactory, parseJsonResponse, successCallback, errorCallback);
};

export const deleteFetchRequest = (url, successCallback, errorCallback) => {
    const requestFactory = () => fetch(`${url}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: buildJsonHeaders(),
    });
    executeRequest(
        requestFactory,
        () => null,
        () => successCallback(),
        errorCallback,
    );
};